import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";

import { useClampPageToTotal } from "../../../hooks/useClampPageToTotal";

export const DEFAULT_PAGE_SIZE = 20;

export const parsePositiveInt = (value: string | null): number | undefined => {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const n = parseInt(value, 10);
  return n > 0 ? n : undefined;
};

const parseIsoDate = (value: string | null): string | undefined =>
  value && dayjs(value).isValid() ? value : undefined;

/**
 * Filtros que valen para todas las pestañas de Sesiones (SCRUM-1167): alumno,
 * grupo y fechas. Viven en la URL, así que buscar a un alumno y pasar de
 * Videos a Juegos sigue mostrando a ese alumno. Los propios de cada pestaña
 * (estado, quiz, recurso) se limpian al cambiar de pestaña.
 */
export const SHARED_FILTER_PARAMS = [
  "userSearch",
  "classroomId",
  "from",
  "to",
] as const;

/**
 * URL como fuente de verdad para filtros y paginación (mismo patrón que
 * Activity/Users): la vista es deep-linkeable y sobrevive refreshes.
 */
export function useSessionFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") ?? "", 10) || 1;
  const pageSize =
    parseInt(searchParams.get("pageSize") ?? "", 10) || DEFAULT_PAGE_SIZE;
  const userSearch = searchParams.get("userSearch") ?? "";
  const classroomId = parsePositiveInt(searchParams.get("classroomId"));
  const from = parseIsoDate(searchParams.get("from"));
  const to = parseIsoDate(searchParams.get("to"));

  // Cualquier cambio de filtro resetea a la página 1 — evita quedar en una
  // página vacía tras achicar el resultado.
  const setFilterParams = useCallback(
    (entries: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(entries).forEach(([key, value]) => {
            if (value !== undefined && value !== "") next.set(key, value);
            else next.delete(key);
          });
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (newPage: number, newPageSize: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(newPage));
        next.set("pageSize", String(newPageSize));
        return next;
      });
    },
    [setSearchParams],
  );

  const hasSharedFilters =
    Boolean(userSearch) || classroomId !== undefined || Boolean(from || to);

  return {
    searchParams,
    page,
    pageSize,
    userSearch,
    classroomId,
    from,
    to,
    hasSharedFilters,
    setFilterParams,
    setPage,
  };
}

/**
 * Si la página actual queda fuera de rango tras un filtro fuerte, retroceder a
 * la última página con datos.
 */
export function useClampSessionPage(
  total: number,
  page: number,
  pageSize: number,
  setPage: (page: number, pageSize: number) => void,
) {
  const onPageChange = useCallback(
    (lastPage: number) => setPage(lastPage, pageSize),
    [setPage, pageSize],
  );
  useClampPageToTotal({ total, pageSize, page, onPageChange });
}
